import React, { useState, useMemo } from 'react';
import { Button, Calendar, Card, Modal, Popover, Tag, Typography } from 'antd';
import { PlusOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const DEFAULT_COLORS = ['#667eea', '#f59e0b', '#22c55e', '#ef4444', '#a855f7', '#06b6d4'];

/**
 * TaskCalendar — Vazifalar Kalendari
 *
 * Props:
 *   events         : object[]   — vazifalar massivi
 *   dateKey        : string     — sanani belgilovchi maydon, 'YYYY-MM-DD' (default: 'date')
 *   titleKey       : string     — sarlavha maydoni                        (default: 'title')
 *   colorKey       : string     — rang maydoni (ixtiyoriy)                (default: 'color')
 *   rowKey         : string     — noyob kalit maydoni                     (default: 'id')
 *   renderEvent    : (event) => ReactNode   — maxsus event ko'rinishi
 *   onEventClick   : (event) => void        — event bosilganda
 *   AddComponent   : ReactComponent — modal forma: { date, onClose, onSuccess }
 *   addModalTitle  : string | (date) => string
 *   onRefresh      : () => void
 */
const TaskCalendar = ({
  events = [],
  dateKey = 'date',
  titleKey = 'title',
  colorKey = 'color',
  rowKey = 'id',
  renderEvent,
  onEventClick,
  AddComponent,
  addModalTitle,
  onRefresh,
  height = '80vh',
}) => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [addOpen, setAddOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const isAddValid = typeof AddComponent === 'function';

  /* events ni sana bo'yicha guruhlash */
  const eventsByDate = useMemo(() => {
    const map = {};
    (Array.isArray(events) ? events : []).forEach((ev) => {
      const key = ev[dateKey] ? dayjs(ev[dateKey]).format('YYYY-MM-DD') : null;
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [events, dateKey]);

  const resolvedModalTitle = useMemo(() => {
    if (!selectedDate) return "Yangi vazifa qo'shish";
    return typeof addModalTitle === 'function'
      ? addModalTitle(selectedDate)
      : addModalTitle || `${selectedDate.format('DD MMMM YYYY')} — yangi vazifa`;
  }, [addModalTitle, selectedDate]);

  const handleClose = () => { setAddOpen(false); setSelectedDate(null); };
  const handleSuccess = () => { setAddOpen(false); setSelectedDate(null); onRefresh?.(); };

  const openAdd = (date) => {
    if (!isAddValid) return;
    setSelectedDate(date);
    setAddOpen(true);
  };

  /* ── Katakning ichidagi render ── */
  const dateCellRender = (date) => {
    const key = date.format('YYYY-MM-DD');
    const dayEvents = eventsByDate[key] || [];

    return (
      <div style={{ position: 'relative', minHeight: "11.5vh" }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {dayEvents.slice(0, 3).map((ev, idx) => {
            const color = ev[colorKey] || DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
            const content = renderEvent ? renderEvent(ev) : (
              <Popover
                key={ev[rowKey]}
                content={
                  <div style={{ maxWidth: 220 }}>
                    <Text strong>{ev[titleKey]}</Text>
                    {ev.description && (
                      <div><Text type="secondary" style={{ fontSize: 12 }}>{ev.description}</Text></div>
                    )}
                    {ev.time && (
                      <div><Text type="secondary" style={{ fontSize: 12 }}>🕐 {ev.time}</Text></div>
                    )}
                  </div>
                }
                trigger="click"
              >
                <Tag
                  color={color}
                  style={{
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    marginBottom: 1,
                    fontSize: 11,
                    lineHeight: '18px',
                    padding: '0 4px',
                  }}
                  onClick={(e) => { e.stopPropagation(); onEventClick?.(ev); }}
                >
                  {ev[titleKey]}
                </Tag>
              </Popover>
            );
            return <div key={ev[rowKey]}>{content}</div>;
          })}

          {dayEvents.length > 3 && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              +{dayEvents.length - 3} ta yana
            </Text>
          )}
        </div>

        {isAddValid && (
          <div
            className="task-cal-add-btn"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              opacity: 0,
              transition: 'opacity 0.15s',
            }}
            onClick={(e) => { e.stopPropagation(); openAdd(date); }}
          >
            <PlusOutlined style={{ fontSize: 12, color: '#667eea' }} />
          </div>
        )}
      </div>
    );
  };

  /* ── Oylik sarlavha ── */
  const headerRender = ({ value, onChange }) => {
    const month = value.format('MMMM YYYY');
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Text strong style={{ fontSize: 16 }}>{month}</Text>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size="large"
            icon={<LeftOutlined />}
            onClick={() => onChange(value.subtract(1, 'month'))}
          />
          <Button
            size="large"
            onClick={() => onChange(dayjs())}
          >
            Bugun
          </Button>
          <Button
            size="large"
            icon={<RightOutlined />}
            onClick={() => onChange(value.add(1, 'month'))}
          />
          {isAddValid && (
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => openAdd(currentDate)}
            >
              Qo&apos;shish
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .task-calendar-wrap .ant-picker-calendar-date:hover .task-cal-add-btn {
          opacity: 1 !important;
        }
        .task-calendar-wrap .ant-picker-calendar-date-content {
          height: auto !important;
          min-height: 60px;
        }
      `}</style>

      <Card className="task-calendar-wrap" style={{ borderRadius: 14 }} styles={{ body: { padding: 0 } }}>
        <Calendar
          value={currentDate}
          onChange={setCurrentDate}
          cellRender={dateCellRender}
          headerRender={headerRender}
          style={{ borderRadius: 14 }}
          onSelect={(date, { source }) => {
            if (source === 'date') setCurrentDate(date);
          }}
        />
      </Card>

      {isAddValid && (
        <Modal
          open={addOpen}
          title={resolvedModalTitle}
          onCancel={handleClose}
          footer={null}
          destroyOnHidden
        >
          <AddComponent
            date={selectedDate}
            dateString={selectedDate?.format('YYYY-MM-DD')}
            onClose={handleClose}
            onSuccess={handleSuccess}
          />
        </Modal>
      )}
    </>
  );
};

export default TaskCalendar;
